import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { styled } from "@mui/material/styles";
import TableHead from "@mui/material/TableHead";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Blogs.css";
import Stack from "@mui/material/Stack";
import StyledButton from "../../Components/StyledButton";
import { Link } from "react-router-dom";
import InputBase from "@mui/material/InputBase";
import FormControl from "@mui/material/FormControl";
import Loader from "../../Components/Loader";
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const TextField = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(3),
  },
  "& .MuiInputBase-input": {
    borderRadius: 4,
    position: "relative",
    border: "1px solid",
    borderColor: "#E0E3E7",
    fontSize: 16,
    padding: "10px 12px",
    "&:focus": {
      borderColor: "#64748b",
    },
  },
}));

const Blogs = () => {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [rows, setRows] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFilteredRows(
      rows?.filter((row) => {
        return (
          row?.title?.toLowerCase()?.includes(searchInput.toLowerCase()) ||
          getDate(row?.createdAt)
            ?.toLowerCase()
            ?.includes(searchInput.toLowerCase()) ||
          getDate(row?.updatedAt)
            ?.toLowerCase()
            ?.includes(searchInput.toLowerCase())
        );
      })
    );
  }, [rows, searchInput]);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const authToken = localStorage.getItem("authToken");
        if (!authToken) navigate("/sign-in");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/v1/api/admin/get-all-blogs`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        response.status === 200 && setIsLoading(false);
        setRows(
          response?.data?.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        );
      } catch (error) {
        if (error?.response?.data?.error === "failed to authenticate token")
          navigate("/sign-in");
      }
    }

    fetchData();
  }, [navigate]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.white,
      color: "#64748b",
      padding: "5px 10px",
      fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: "10px 10px",
    },
  }));

  const getDate = (date) => {
    const inputDate = new Date(date);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return inputDate.toLocaleDateString("en-US", options);
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <div className="blog-container">
        <div className="blog-header">
          <p>
            <Link
              to="/dashboard"
              style={{
                textDecoration: "none",
                color: "#454545",
                borderBottom: "1px solid transparent", // Initial border
                transition: "border-bottom 0.3s ease", // Add smooth transition
              }}
              onMouseOver={(e) =>
                (e.target.style.borderBottom = "1px solid #454545")
              } // Show underline on hover
              onMouseOut={(e) =>
                (e.target.style.borderBottom = "1px solid transparent")
              } // Hide underline on mouseout
            >
              Home
            </Link>{" "}
            / <strong>Blogs</strong>
          </p>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <h4 style={{ fontSize: '1.5rem' }}>Add Blogs</h4>
            <Link to="/create-blog">
              <StyledButton text={"Add"} />
            </Link>
          </Stack>
        </div>
        <FormControl variant="standard" sx={{ margin: "15px 0 5px 0" }}>
          <TextField
            value={searchInput}
            margin="dense"
            id="name"
            label="Title"
            type="text"
            size="small"
            variant="outlined"
            placeholder="Search"
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </FormControl>
        <div className="table">
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: 800, borderColor: "#E0E0E0" }}
              aria-label="custom pagination table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                    Published Date
                  </StyledTableCell>
                  <StyledTableCell>Title</StyledTableCell>
                  <StyledTableCell>Category</StyledTableCell>
                  <StyledTableCell>Sub Category</StyledTableCell>
                  <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                    Last Update
                  </StyledTableCell>
                  <StyledTableCell>View</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(rowsPerPage > 0
                  ? filteredRows?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : filteredRows
                )?.map((row) => (
                  <TableRow key={row.id}>
                    <StyledTableCell style={{ width: 100 }}>
                      {getDate(row.createdAt)}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.title}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{ width: 120, whiteSpace: "nowrap" }}
                    >
                      {row.Category?.name}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{ width: 120, whiteSpace: "nowrap" }}
                    >
                      {row.Subcategory?.name}
                    </StyledTableCell>
                    <StyledTableCell
                      style={{ width: 100, whiteSpace: "nowrap" }}
                    >
                      {getDate(row.updatedAt)}
                    </StyledTableCell>
                    <TableCell
                      style={{
                        width: 60,
                        textAlign: "center",
                        padding: "2px 10px",
                        color: "#64748b",
                      }}
                    >
                      <Link to={`/blog-details/${row?.id}`}>
                        <VisibilityIcon
                          sx={{
                            color: "#64748b",
                            cursor: "pointer",
                          }}
                        />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 20]}
                    colSpan={6}
                    count={filteredRows?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        "aria-label": "rows per page",
                      },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                    labelRowsPerPage={"Items per page:"}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </div>
      </div>
    </>
  );
};

export default Blogs;
